import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export type ThemePreference = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

// Also read by the no-flash inline script in public/index.html — keep in sync.
const STORAGE_KEY = "pnai-theme";
const LIGHT_DAISY_THEME = "mytheme";
const DARK_DAISY_THEME = "dark";

interface ThemeContextType {
  /** The user's saved preference. */
  theme: ThemePreference;
  /** What is actually applied ("system" resolved against the OS setting). */
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

function readStoredPreference(): ThemePreference {
  const value = localStorage.getItem(STORAGE_KEY);
  return value === "light" || value === "dark" || value === "system"
    ? value
    : "system";
}

// Set on <html>, not a wrapper div, because DaisyUI <dialog class="modal">
// portals render outside the React root.
function applyDomTheme(resolved: ResolvedTheme) {
  document.documentElement.setAttribute(
    "data-theme",
    resolved === "dark" ? DARK_DAISY_THEME : LIGHT_DAISY_THEME
  );
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", resolved === "dark" ? "#1d232a" : "#ffffff");
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [theme, setThemeState] = useState<ThemePreference>(
    readStoredPreference
  );
  const [systemDark, setSystemDark] = useState(
    () => window.matchMedia("(prefers-color-scheme: dark)").matches
  );

  // Follow live OS theme changes while in "system" mode.
  useEffect(() => {
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  // Sync preference changes made in other tabs.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setThemeState(readStoredPreference());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const resolvedTheme: ResolvedTheme =
    theme === "system" ? (systemDark ? "dark" : "light") : theme;

  useEffect(() => {
    applyDomTheme(resolvedTheme);
  }, [resolvedTheme]);

  const value = useMemo(
    () => ({
      theme,
      resolvedTheme,
      setTheme: (t: ThemePreference) => {
        localStorage.setItem(STORAGE_KEY, t);
        setThemeState(t);
      },
    }),
    [theme, resolvedTheme]
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

/**
 * Forces light theme while mounted — for shareable/marketing pages whose
 * colors are tuned for a white background. Restores the user's theme on
 * unmount.
 */
export const ForceLightTheme: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { resolvedTheme } = useTheme();
  useEffect(() => {
    applyDomTheme("light");
    return () => applyDomTheme(resolvedTheme);
  }, [resolvedTheme]);
  return <>{children}</>;
};
